
import React from 'react';
import { format } from 'date-fns';
import { pl, enUS } from 'date-fns/locale';
import { useLanguage } from '@/hooks/useLanguage';
import { Badge } from '@/components/ui/badge';
import { ShieldAlert } from 'lucide-react';
import { VulnerabilityItem } from '@/types/dashboard';

interface VulnerabilityItemProps {
  vulnerability: VulnerabilityItem;
  severityColor?: string;
  badgeVariant?: "default" | "secondary" | "destructive" | "outline";
  badgeClassName?: string;
  Icon?: React.ComponentType<{ className?: string }>;
}

export function VulnerabilityListItem({
  vulnerability,
  severityColor = "text-destructive",
  badgeVariant = "destructive",
  badgeClassName = "",
  Icon = ShieldAlert
}: VulnerabilityItemProps) {
  const { language, t } = useLanguage();

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd MMM yyyy', {
        locale: language === 'pl' ? pl : enUS,
      });
    } catch (error) {
      return dateString;
    }
  };

  // Get the translated description based on language
  const getLocalizedDescription = () => {
    if (language === 'pl' && vulnerability.description_pl) {
      return vulnerability.description_pl;
    }
    return vulnerability.description || t('common.no_description');
  };

  return (
    <div className="space-y-1 h-full flex flex-col justify-center p-2">
      <div className="flex justify-between items-center mb-1 shrink-0">
        <div className="flex items-center min-w-0 flex-1 overflow-hidden mr-2">
          <Icon className={`h-4 w-4 mr-2 flex-shrink-0 ${severityColor}`} />
          <h3 className="font-medium truncate flex-shrink-0 max-w-[50%]">{vulnerability.id || "CVE-unknown"}</h3>
          <span className="text-[10px] text-muted-foreground ml-2 truncate pt-0.5">
            {formatDate(vulnerability.publishedDate)}
          </span>
        </div>
        <Badge variant={badgeVariant as any} className={`ml-2 text-xs flex-shrink-0 ${badgeClassName}`}>
          {vulnerability.score.toFixed(1)}
        </Badge>
      </div>
      <div className="text-xs text-muted-foreground flex-grow min-h-0">
        <span className="line-clamp-2">{getLocalizedDescription()}</span>
      </div>
    </div>
  );
}
